import { authFetch } from '@/services/apiClient'
import { DirectUploadUnavailable, uploadDirect } from '@/services/uploadClient'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface IndividualProfile {
  individual_id: number
  first_name: string
  last_name: string
  phone: string | null
  province: string | null
  avatar_url: string | null
  bio: string | null
  date_of_birth: string | null
  resume_url: string | null          // ลิงก์เก่าที่เคยวางไว้ (ถ้ามี)
  has_resume_file: boolean           // มีไฟล์เรซูเม่ที่อัปโหลด (ส่วนตัว ดาวน์โหลดผ่าน downloadResume)
  linkedin_url: string | null
  current_job_title: string | null
  years_experience: number
  is_open_to_work: boolean
  email: string
  is_active: boolean
  is_verified: boolean
  last_login: string | null
}

export interface CompanyProfile {
  company_id: number
  company_name: string
  logo_url: string | null
  industry: string | null
  company_size: string | null
  description: string | null
  website: string | null
  location: string | null
  tax_id: string | null
  is_verified: boolean
  email: string
  is_active: boolean
  last_login: string | null
}

export type Profile = IndividualProfile | CompanyProfile

async function parseJson<T>(res: Response): Promise<ApiResponse<T>> {
  try {
    return await res.json()
  } catch {
    return { success: false, error: 'เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ' }
  }
}

export const getProfile = async (): Promise<Profile> => {
  const res = await authFetch('/profile')
  const json = await parseJson<Profile>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'โหลดโปรไฟล์ไม่สำเร็จ')
  return json.data
}

export const updateProfile = async (data: Partial<IndividualProfile & CompanyProfile>): Promise<Profile> => {
  const res = await authFetch('/profile', { method: 'PUT', body: JSON.stringify(data) })
  const json = await parseJson<Profile>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'บันทึกโปรไฟล์ไม่สำเร็จ')
  return json.data
}

// เรซูเม่ (PDF/DOC/DOCX ≤ 10 MB) เก็บส่วนตัวบนที่เก็บไฟล์ — แทนที่ไฟล์/ลิงก์เดิม · ส่งตรงไปที่เก็บด้วย Uppy แล้วให้ API ตรวจ/บันทึก
// API ยังไม่มีอัปโหลดตรง (deploy หน้าเว็บก่อน API) → ถอยไปทางเดิม (request.files ≤ 5 MB) อัตโนมัติ
export const uploadResume = async (file: File, onProgress?: (percent: number) => void): Promise<Profile> => {
  try {
    return await uploadDirect<Profile>(file, { purpose: 'PROFILE_RESUME', onProgress })
  } catch (e) {
    if (!(e instanceof DirectUploadUnavailable)) throw e
    return uploadResumeLegacy(file)
  }
}

const uploadResumeLegacy = async (file: File): Promise<Profile> => {
  const form = new FormData()
  form.append('file', file)
  const res = await authFetch('/profile/resume', { method: 'POST', body: form })
  const json = await parseJson<Profile>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'อัปโหลดเรซูเม่ไม่สำเร็จ')
  return json.data
}

export const deleteResume = async (): Promise<Profile> => {
  const res = await authFetch('/profile/resume', { method: 'DELETE' })
  const json = await parseJson<Profile>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ลบเรซูเม่ไม่สำเร็จ')
  return json.data
}

// ดาวน์โหลดเรซูเม่ของตัวเอง (ไฟล์ส่วนตัว ต้องแนบ token จึงโหลดเป็น blob)
export const downloadResume = async (): Promise<void> => {
  const res = await authFetch('/profile/resume')
  if (!res.ok) {
    const json = await parseJson<unknown>(res)
    throw new Error(json.error || json.message || 'ดาวน์โหลดเรซูเม่ไม่สำเร็จ')
  }
  const disposition = res.headers.get('Content-Disposition') || ''
  const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1] || 'Resume'
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = decodeURIComponent(name)
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

// อัปโหลดรูป (PNG/JPG/WEBP ≤ 10 MB) → คืน URL ไปเก็บใน avatar_url / logo_url · ส่งตรงไปที่เก็บด้วย Uppy แล้วให้ API ตรวจ/ย้ายเข้า images/ (สาธารณะ)
// API ยังไม่มีอัปโหลดตรง → ถอยไปทางเดิม (request.files ≤ 2 MB) อัตโนมัติ
export const uploadImage = async (file: File, onProgress?: (percent: number) => void): Promise<string> => {
  try {
    return (await uploadDirect<{ url: string }>(file, { purpose: 'IMAGE', onProgress })).url
  } catch (e) {
    if (!(e instanceof DirectUploadUnavailable)) throw e
    return uploadImageLegacy(file)
  }
}

const uploadImageLegacy = async (file: File): Promise<string> => {
  const form = new FormData()
  form.append('file', file)
  const res = await authFetch('/upload/image', { method: 'POST', body: form })
  const json = await parseJson<{ url: string }>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'อัปโหลดรูปไม่สำเร็จ')
  return json.data.url
}
